/**
 * The rest of today, in the order it happens
 * ==========================================
 *
 * Constitution §6 Phase 2 item 1 (target §20/§21, invariant 21). This replaces
 * the proximity-grouped ledger ("Around Now" / "Other Habits" / "Other Tasks"),
 * which read as four piles rather than a day, and promotes the read-only
 * Timeline card into the surface the user can actually act from.
 *
 * WHAT THIS COMPONENT MAY NOT DO (target §21, and this repo's own scar tissue —
 * see the TodaysReleaseCard demotion note in TodayFocus.tsx: two engines
 * answering one question on one screen is not allowed):
 *   - it may not order by anything but time (the one sort lives in
 *     timelineModel.ts and is pinned by a unit test),
 *   - it may not decide membership — every row comes from upstream,
 *   - it may not decide CORE/OPTIONAL — that is the day-open snapshot's answer,
 *     arriving as `plan_role` on the read model,
 *   - it may not derive Flexible/Confirmed — that is `commitment`, resolved
 *     server-side by scheduling_policy.resolved_commitment.
 *
 * SC-6 (zero-nag): the verbs live behind a tap. There is no count of
 * unconfirmed holds, no "confirm your day" prompt, and no follow-up after a
 * skip. A Flexible block the user ignores forever is a valid way to live.
 * SC-21 (KEEP_FREE): an empty rest-of-day is a good outcome, said plainly, with
 * nothing offered to fill it.
 * SC-36: the user sees Flexible / Confirmed / Later. Never commitment_source,
 * block_status, or any other internal word.
 */

import { useEffect, useMemo, useState } from 'react'
import { formatDuration } from '../../utils/dayShape'
import { ChevronDown, Clock, Loader2, Lock, LockOpen, Moon } from 'lucide-react'
import { COPY } from '../../utils/todayOneClearThing'
import {
  anchorMinutes,
  buildExecutionTimeline,
} from './timelineModel'
import type {
  TimelineDefinitionLike,
  TimelineOccurrenceLike,
  TimelineRow,
  TimelineSourceEvent,
  TimelineSpotlightRef,
  TimelineTaskLike,
} from './timelineModel'
// The verbs ride the clients the scheduling lanes already own — one client
// per endpoint family, so /today and ScheduleV2 cannot drift apart on what
// "confirm" or "skip today" does.
import { confirmHolds, moveBlock, removeBlockFromDay } from '../../services/aiSchedulerService'
import type { VerbResult } from '../../services/aiSchedulerService'
import { fetchBlockWhy } from '../../services/blockWhyService'
import type { BlockWhy } from '../../services/blockWhyService'
import { restOfDayWords } from '../../utils/oriAsks'

export interface ExecutionTimelineProps {
  /**
   * Live options the prepared-day curtain is holding. Only used to decide
   * whether an empty schedule may be called "clear" — the timeline itself
   * never renders them, that is the one ranked ask zone's job.
   */
  liveOptions?: number
  events: TimelineSourceEvent[]
  tasks: TimelineTaskLike[]
  habitOccurrences: TimelineOccurrenceLike[]
  habitDefinitions: TimelineDefinitionLike[]
  /** Minutes from midnight, supplied by the page so this stays clock-free. */
  nowMinutes: number
  /** The user's own "HH:MM" sleep anchor, or null when it is not known. */
  sleepAnchor: string | null
  /** The day these rows sit on, "YYYY-MM-DD" — the day-scoped verbs name it. */
  localDate: string
  /** The one job the page is already showing on the hero card, so the timeline
   *  does not draw it a second time. */
  spotlight?: TimelineSpotlightRef | null
  onOpenRow?: (row: TimelineRow) => void
  onStart?: (row: TimelineRow) => void
  onDone?: (row: TimelineRow) => void
  /** Called after a verb changed server state, so the page refetches its day. */
  onChanged?: () => void
}

/** How a recorded commitment source is said out loud (SC-3: the server hands
 *  over machine facts, the surface phrases them; SC-36: no internal words). */
const SOURCE_COPY: Record<string, string> = {
  user_direct: COPY.whySourceUserDirect,
  user_confirmation: COPY.whySourceUserConfirmation,
  ai_proposal: COPY.whySourceAiProposal,
  user_recurring_rule: COPY.whySourceRule,
  admitted_habit_rule: COPY.whySourceRule,
}

function CommitmentChip({ state }: { state: 'flexible' | 'confirmed' }) {
  // Ratified decision #2: the words are Flexible / Confirmed and the affordance
  // is the existing 🔓 / 🔒. Flexible is deliberately the quieter of the two.
  return state === 'confirmed' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-subtle text-brand text-app-micro font-semibold">
      <Lock className="w-3 h-3" aria-hidden="true" /> {COPY.confirmed}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-soft text-muted text-app-micro">
      <LockOpen className="w-3 h-3" aria-hidden="true" /> {COPY.flexible}
    </span>
  )
}

function PlanRoleChip({ role }: { role: TimelineRow['planRole'] }) {
  if (role === 'none') return null
  const tone = role === 'core'
    ? 'bg-brand-subtle text-brand'
    : role === 'optional'
      ? 'bg-surface-alt text-secondary'
      : 'bg-ok/10 text-ok'
  const label = role === 'core' ? COPY.planCore : role === 'optional' ? COPY.planOptional : COPY.planHabit
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-app-micro font-semibold uppercase tracking-wide ${tone}`}>
      {label}
    </span>
  )
}

type RowStatus = { tone: 'ok' | 'wait' | 'bad'; text: string } | null

function WhyPanel({ blockId }: { blockId: number }) {
  const [why, setWhy] = useState<BlockWhy | null>(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchBlockWhy(blockId)
      .then(w => { if (!cancelled) setWhy(w) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [blockId])

  if (loading) {
    return (
      <p className="mt-2 flex items-center gap-1.5 text-app-micro text-muted">
        <Loader2 className="w-3 h-3 animate-spin motion-reduce:animate-none" aria-hidden="true" /> {COPY.whyLoading}
      </p>
    )
  }
  if (!why) return <p className="mt-2 text-app-micro text-muted">{COPY.whyUnavailable}</p>

  // An unstamped pair is Horton's inference about an older row, never a
  // recorded act — saying "you confirmed this" about a derivation would lie.
  const sourceLine = why.commitmentStamped && why.commitmentSource
    ? SOURCE_COPY[why.commitmentSource]
    : COPY.whySourceDerived
  return (
    <div className="mt-2 rounded-lg bg-surface-alt px-2.5 py-2 space-y-1">
      {sourceLine && <p className="text-app-micro text-secondary leading-relaxed">{sourceLine}</p>}
      {why.parent?.title && (
        <p className="text-app-micro text-muted">{COPY.whyPartOf(why.parent.title)}</p>
      )}
      {why.parent?.dueDate && (
        <p className="text-app-micro text-muted">{COPY.whyDue(why.parent.dueDate.slice(0, 10))}</p>
      )}
    </div>
  )
}

function MoveForm({ row, busy, onCancel, onMove }: {
  row: TimelineRow
  busy: boolean
  onCancel: () => void
  onMove: (timeSlot: string, keepFlexible: boolean) => void
}) {
  const initial = row.timeLabel.slice(0, 5)
  const [start, setStart] = useState(/^\d{2}:\d{2}$/.test(initial) ? initial : '')
  // SC-7: moving a block confirms it by default; this is the ratified secondary
  // affordance that moves it and leaves it Horton's to re-plan.
  const [keepFlexible, setKeepFlexible] = useState(row.commitment === 'flexible')
  const minutes = row.durationMinutes
  const slot = useMemo(() => {
    if (!/^\d{1,2}:\d{2}$/.test(start)) return null
    if (!minutes || minutes <= 0) return start
    const [h, m] = start.split(':').map(Number)
    const end = h * 60 + m + minutes
    if (end >= 24 * 60) return start
    return `${start}-${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`
  }, [start, minutes])

  return (
    <div className="mt-2 rounded-lg border border-soft bg-surface-alt p-2.5 space-y-2">
      <label className="flex items-center gap-2 text-app-micro text-secondary">
        {COPY.moveTo}
        <input
          type="time"
          value={start}
          onChange={e => setStart(e.target.value)}
          className="min-h-touch rounded-lg border border-soft bg-surface px-2 text-app-meta text-primary"
        />
      </label>
      <label className="flex items-center gap-2 text-app-micro text-secondary">
        <input
          type="checkbox"
          checked={keepFlexible}
          onChange={e => setKeepFlexible(e.target.checked)}
          className="w-4 h-4 accent-current"
        />
        {COPY.moveKeepFlexible}
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!slot || busy}
          onClick={() => slot && onMove(slot, keepFlexible)}
          className="min-h-touch px-3 rounded-lg bg-brand text-on-brand text-app-micro font-semibold disabled:opacity-50"
        >
          {busy ? COPY.moveSaving : COPY.moveConfirmLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-touch px-3 rounded-lg border border-soft text-app-micro text-secondary"
        >
          {COPY.moveCancel}
        </button>
      </div>
    </div>
  )
}

function Verb({ label, onClick, disabled, tone = 'quiet' }: {
  label: string
  onClick: () => void
  disabled?: boolean
  tone?: 'quiet' | 'brand' | 'ok'
}) {
  const cls = tone === 'brand'
    ? 'border-brand-subtle text-brand'
    : tone === 'ok'
      ? 'border-ok text-ok'
      : 'border-soft text-secondary'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`min-h-touch px-3 rounded-lg border bg-surface text-app-micro font-medium disabled:opacity-50 transition-colors ${cls}`}
    >
      {label}
    </button>
  )
}

function RowCard({ row, open, onToggle, onOpenRow, onStart, onDone, onChanged, localDate }: {
  row: TimelineRow
  open: boolean
  onToggle: () => void
  onOpenRow?: (row: TimelineRow) => void
  onStart?: (row: TimelineRow) => void
  onDone?: (row: TimelineRow) => void
  onChanged?: () => void
  localDate: string
}) {
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<RowStatus>(null)
  const [moving, setMoving] = useState(false)
  const [whyOpen, setWhyOpen] = useState(false)
  const flexible = row.commitment === 'flexible'
  const confirmed = row.commitment === 'confirmed'
  const canAct = row.blockId !== null && !row.completed

  // Flexible must FEEL softer than Confirmed (target §20/§40): a dashed, quieter
  // card against a solid one. The distinction is the whole point of HOLD-first.
  const shell = row.completed
    ? 'border-soft bg-surface-alt/60 opacity-70'
    : flexible
      ? 'border-dashed border-soft bg-surface-alt/40'
      : 'border-soft bg-surface'

  /**
   * Run one verb and say honestly what happened.
   *
   * `ok && !applied` is the 202 `applied:false` contract — the server took
   * the request and has NOT changed the day yet. That renders as pending; it
   * never renders as done, and it never refreshes the page as if it had.
   */
  const run = async (fn: () => Promise<VerbResult>, okText: string) => {
    setBusy(true)
    setStatus(null)
    try {
      const r = await fn()
      if (r.ok && r.applied) {
        setStatus({ tone: 'ok', text: okText })
        onChanged?.()
      } else if (r.ok) {
        setStatus({ tone: 'wait', text: r.message || COPY.verbPending })
      } else {
        setStatus({ tone: 'bad', text: r.error || COPY.verbFailed })
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`rounded-xl border p-3 transition-colors ${shell}`}>
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => onOpenRow?.(row)}
          className="flex-1 min-w-0 text-left"
        >
          {/* Chips first, then the title, then ONE meta line in the page's
              metadata contract ("45 min · 09:00–10:00", North Star rule 4) —
              the same line every other task row on /today carries. The time
              used to lead as its own label and the duration read "45m". */}
          {(row.planRole || row.commitment) && (
            <div className="flex flex-wrap items-center gap-1.5">
              <PlanRoleChip role={row.planRole} />
              {row.commitment && <CommitmentChip state={row.commitment} />}
            </div>
          )}
          <p className={`mt-1 text-app-body font-medium ${row.completed ? 'text-muted' : 'text-primary'}`}>
            {row.title}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-app-micro text-muted tabular-nums">
            {(formatDuration(row.durationMinutes) || row.timeLabel) && (
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" aria-hidden="true" />
                {[formatDuration(row.durationMinutes), row.timeLabel].filter(Boolean).join(' · ')}
              </span>
            )}
            {row.calendarName && <span>{row.calendarName}</span>}
            {row.inProgress && <span className="text-brand">{COPY.rowInMotion}</span>}
          </div>
        </button>
        {!row.completed && (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${row.title}`}
            className="min-h-touch min-w-touch flex items-center justify-center rounded-lg text-muted hover:bg-surface-alt transition-colors"
          >
            <ChevronDown className={`w-4 h-4 transition-transform${open ? ' rotate-180' : ''}`} aria-hidden="true" />
          </button>
        )}
      </div>

      {open && !row.completed && (
        <div className="mt-2 space-y-2">
          <p className="text-app-micro text-muted leading-relaxed">
            {flexible ? COPY.flexibleMeaning : confirmed ? COPY.confirmedMeaning : COPY.unscheduledMeaning}
          </p>
          <div className="flex flex-wrap gap-2">
            {flexible && canAct && (
              <>
                <Verb
                  label={COPY.verbConfirm}
                  tone="brand"
                  disabled={busy}
                  onClick={() => run(async () => {
                    // Always scoped to THIS block: an unscoped confirm-holds
                    // body would sweep every outstanding hold the user owns.
                    const r = await confirmHolds({ block_ids: [row.blockId as number] })
                    // A 200 that confirmed nothing is not a success. Two
                    // different nothings: the day's ceiling refused the hold
                    // (the server says why), or the hold is no longer there to
                    // confirm — a stale row, not a rejection.
                    if (r.ok && r.applied && r.confirmed_count === 0) {
                      const reason = r.rejected[0]?.reason
                      return reason
                        ? { ok: false, applied: false, error: reason }
                        : { ok: true, applied: false, message: COPY.verbConflict }
                    }
                    return r
                  }, COPY.verbConfirmed)}
                />
                <Verb
                  label={COPY.verbKeepFlexible}
                  disabled={busy}
                  // Already Flexible — there is nothing to write. Zero-nag
                  // (SC-6) means "leave it alone" is a real answer to the
                  // Confirm offer, not a workflow of its own, so this closes
                  // the ask without a request and without a follow-up.
                  onClick={() => setStatus({ tone: 'ok', text: COPY.verbKeptFlexible })}
                />
              </>
            )}
            {(confirmed || !row.commitment) && row.itemType === 'task' && onStart && (
              <Verb label={COPY.verbStart} tone="brand" disabled={busy} onClick={() => onStart(row)} />
            )}
            {(confirmed || !row.commitment) && onDone && (
              <Verb label={COPY.verbDone} tone="ok" disabled={busy} onClick={() => onDone(row)} />
            )}
            {canAct && (
              <Verb label={COPY.verbMove} disabled={busy} onClick={() => setMoving(v => !v)} />
            )}
            {canAct && (
              <Verb
                label={flexible ? COPY.verbNotToday : COPY.verbSkipToday}
                disabled={busy}
                onClick={() => run(
                  // The block's OWN day when it carries one, else the user's
                  // local day — the server refuses to guess it (SC-35), and a
                  // recurring master needs it to name the occurrence.
                  () => removeBlockFromDay(row.blockId as number, row.blockDate ?? localDate),
                  COPY.verbSkipped,
                )}
              />
            )}
            {flexible && canAct && (
              <Verb label={COPY.verbWhy} disabled={busy} onClick={() => setWhyOpen(v => !v)} />
            )}
          </div>
          {moving && row.blockId !== null && (
            <MoveForm
              row={row}
              busy={busy}
              onCancel={() => setMoving(false)}
              onMove={(timeSlot, keepFlexible) => {
                setMoving(false)
                void run(
                  () => moveBlock(row.blockId as number, {
                    timeSlot,
                    date: row.blockDate ?? localDate,
                    // SC-7: absence of keepFlexible is the ratified
                    // confirm-on-edit default, so only send it when the user
                    // asked to keep the block Horton's.
                    ...(keepFlexible ? { keepFlexible: true } : {}),
                  }),
                  COPY.verbMoved,
                )
              }}
            />
          )}
          {whyOpen && row.blockId !== null && <WhyPanel blockId={row.blockId} />}
          {status && (
            <p
              className={`text-app-micro ${
                status.tone === 'ok' ? 'text-ok' : status.tone === 'wait' ? 'text-warn' : 'text-bad'
              }`}
              role="status"
            >
              {status.text}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export function ExecutionTimeline(props: ExecutionTimelineProps) {
  const {
    events, tasks, habitOccurrences, habitDefinitions, nowMinutes, sleepAnchor, localDate,
    spotlight, liveOptions = 0,
  } = props
  const [openKey, setOpenKey] = useState<string | null>(null)
  const [earlierOpen, setEarlierOpen] = useState(false)

  const model = useMemo(() => buildExecutionTimeline({
    events,
    tasks,
    habitOccurrences,
    habitDefinitions,
    nowMinutes,
    dayEndMinutes: anchorMinutes(sleepAnchor),
    spotlight,
  }), [events, tasks, habitOccurrences, habitDefinitions, nowMinutes, sleepAnchor, spotlight])

  const card = (row: TimelineRow) => (
    <RowCard
      key={row.key}
      row={row}
      open={openKey === row.key}
      onToggle={() => setOpenKey(k => (k === row.key ? null : row.key))}
      onOpenRow={props.onOpenRow}
      onStart={props.onStart}
      onDone={props.onDone}
      onChanged={props.onChanged}
      localDate={localDate}
    />
  )

  // The anchor is a MARKER, never a filter: a row past the user's sleep time
  // still renders, below the line, because hiding their own commitment would
  // be worse than admitting the day ran long.
  const end = model.dayEndMinutes
  const beforeAnchor = end === null ? model.upcoming : model.upcoming.filter(r => (r.startMinutes ?? 0) < end)
  const afterAnchor = end === null ? [] : model.upcoming.filter(r => (r.startMinutes ?? 0) >= end)

  const nothingAhead = model.upcoming.length === 0 && model.anytime.length === 0

  return (
    <div className="space-y-2">
      {model.earlier.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setEarlierOpen(v => !v)}
            aria-expanded={earlierOpen}
            className="min-h-touch text-app-micro text-muted hover:text-secondary inline-flex items-center gap-1"
          >
            {COPY.earlierToday(model.earlier.length)}
            <ChevronDown className={`w-3 h-3 transition-transform${earlierOpen ? ' rotate-180' : ''}`} aria-hidden="true" />
          </button>
          {earlierOpen && <div className="mt-2 space-y-2 opacity-70">{model.earlier.map(card)}</div>}
        </div>
      )}

      <div className="relative border-t border-brand" aria-hidden="true">
        <span className="absolute -top-2 right-0 bg-surface px-1 text-app-micro text-brand">{COPY.now}</span>
      </div>

      {nothingAhead ? (
        // SC-21: empty time is the outcome, not a gap to be filled. No CTA here.
        //
        // But it is only "clear" if nothing is on offer either. This read only
        // scheduled blocks, so the page said "The rest of today is clear" while
        // the curtain held three live options one zone below — both true, and
        // together a contradiction the owner hit on 2026-09-08. It now counts
        // what is actually being offered and names it instead of denying it.
        <p className="pt-2 text-app-meta text-secondary">{restOfDayWords(liveOptions)}</p>
      ) : (
        <>
          {beforeAnchor.map(card)}
          {sleepAnchor && (beforeAnchor.length > 0 || afterAnchor.length > 0) && (
            <p className="flex items-center gap-1.5 pt-1 text-app-micro text-muted">
              <Moon className="w-3 h-3" aria-hidden="true" /> {COPY.windsDown(sleepAnchor)}
            </p>
          )}
          {afterAnchor.map(card)}
          {model.anytime.length > 0 && (
            <div className="pt-2 space-y-2">
              <p className="text-app-micro font-medium text-secondary">{COPY.anytimeToday}</p>
              {model.anytime.map(card)}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ExecutionTimeline
